import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { useState, useContext, useEffect } from 'react';
import { InputAdornment, IconButton, Box, Typography, TextField, Alert, Button } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { IconContext, TResetPasswordRequest, AuthContext } from '@plumeuk/shapeshift-identity';
import { useValidate } from '@plumeuk/use-validate';
import 'x2js';

const resetPasswordRequestValidationDoc = {
    password: [
        "required"
    ],
    passwordConfirmation: [
        "required"
    ]
};

const replaceAll = (str, find, replace) => {
    // Escape special characters in `find` using a regular expression
    const escapedFind = find.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    // Create a regular expression with global flag to replace all occurrences
    const regex = new RegExp(escapedFind, "g");
    // Use the replace method with the regular expression
    return (str ?? "").replace(regex, replace);
};

const useStyles$1 = makeStyles()((_theme, { hasError, valid }) => ({
    inputText: {},
    input: {
        border: valid ? "1px solid #d1d1d1" : "1px solid red",
        padding: "10px 20px",
        marginTop: "8px",
        width: "100%",
        boxSizing: "border-box"
    },
    label: {},
    errorMessage: {
        maxHeight: hasError ? 50 : 0,
        color: "#f30000",
        fontSize: "16px",
        boxSizing: "border-box",
        transition: "max-height 0.15s ease-out",
        "& p": {
            paddingTop: "5px"
        }
    }
}));
const InputText = ({ className, label, value, containerProps, error, onValidate, ...props }) => {
    const [showPassword, setShowPassword] = useState(false);
    const valid = error === undefined;
    const { classes, cx } = useStyles$1({ hasError: !!error, valid });
    const { visibilityIcon, visibilityOffIcon } = useContext(IconContext);
    useEffect(() => {
        if (error !== undefined) {
            onValidate?.();
        }
    }, [value]);
    const inputProps = {
        disableUnderline: true,
        autoCapitalize: "none",
        autoComplete: "off"
    };
    if (props.type === "password") {
        inputProps["endAdornment"] = (jsx(InputAdornment, { position: "end", children: jsx(IconButton, { "data-test-id": "input-text-show-password-button", onClick: () => setShowPassword((prev) => !prev), edge: "end", children: showPassword ? visibilityIcon : visibilityOffIcon }) }));
    }
    return (jsxs(Box, { className: cx(classes.inputText, className), ...containerProps, children: [label && jsx(Typography, { className: classes.label, children: label }), jsx(TextField, { className: classes.input, id: "input-text", variant: "standard", autoComplete: 'off', InputProps: { ...inputProps, ...props.InputProps }, value: value, inputProps: {
                    "data-test-id": "text-input-" + (label ? replaceAll(label?.toLocaleLowerCase(), " ", "-") : ""),
                    autoCapitalize: "none",
                    autoCorrect: "off"
                }, onBlur: e => {
                    props?.onBlur?.(e);
                    onValidate?.();
                }, ...props, type: props.type === "password" && showPassword ? "text" : props.type }), jsx("div", { className: classes.errorMessage, children: jsx("p", { children: error ?? " " }) })] }));
};

/* eslint-disable no-console */
console.log.bind(null) ;
console.warn.bind(null) ;
const DevError = console.error.bind(null) ;

const useStyles = makeStyles()((theme) => ({
    loginForm: {
        maxWidth: "400px",
        borderRadius: "10px",
        backgroundColor: theme.palette.background.paper,
        padding: "60px 80px",
        [theme.breakpoints.down("sm")]: {
            padding: "20px"
        }
    },
    link: {
        color: theme.palette.secondary.main
    },
    title: {
        marginBottom: "45px",
        display: "block",
        textAlign: "center",
        width: "100%",
        [theme.breakpoints.down("sm")]: {
            fontSize: "32px",
            lineHeight: "45px",
            padding: "0 30px",
            boxSizing: "border-box"
        }
    },
    input: {
        marginTop: "10px"
    },
    buttonContainer: {
        marginTop: "40px",
        display: "flex",
        flexDirection: "column",
        gap: "20px",
        "& button": {
            width: "100%",
            height: "51px",
            [theme.breakpoints.down("sm")]: {
                fontSize: "15px"
            }
        }
    },
    googleBtn: {
        fontSize: "19.26px",
        backgroundColor: "white",
        marginTop: "15px",
        color: "grey",
        boxShadow: "0px 0px 3.30435px rgba(0, 0, 0, 0.084), 0px 2.2029px 3.30435px rgba(0, 0, 0, 0.168)",
        "&:hover": {
            backgroundColor: "#d1d1d180"
        },
        "& img": {
            width: "34px",
            marginRight: "8px",
            mixBlendMode: "multiply"
        },
        [theme.breakpoints.down("sm")]: {
            fontSize: "15px",
            marginTop: "20px"
        }
    },
    forgotPassword: {
        color: theme.palette.secondary.main,
        fontSize: "14px",
        fontWeight: 500,
        cursor: "pointer"
    }
}));
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ResetPasswordForm = ({ className, title, code, onLoginLinkClick, ...props }) => {
    const { classes, cx } = useStyles();
    const [formData, setFormData] = useState(TResetPasswordRequest);
    const [validationResult, runValidate, hasErrors, clearValidation] = useValidate(resetPasswordRequestValidationDoc, formData);
    const [postRequestValidation, setPostRequestValidation] = useState({});
    const { resetPassword } = useContext(AuthContext);
    const [requestSent, setRequestSent] = useState(false);
    const [invalidCode, setInvalidCode] = useState(false);
    const handleSubmit = async () => {
        setInvalidCode(false);
        setPostRequestValidation({});
        //to do login should take IResetPasswordRequest
        if (formData.password !== formData.passwordConfirmation) {
            setPostRequestValidation(prev => ({ ...prev, passwordConfirmation: "Passwords do not match" }));
            return;
        }
        resetPassword(code, formData.password, formData.passwordConfirmation)
            .then(() => {
            setRequestSent(true);
        })
            .catch((e) => {
            DevError(e);
            if (e.response?.status === 400)
                setInvalidCode(true);
        });
    };
    useEffect(() => {
        setFormData(TResetPasswordRequest);
        setPostRequestValidation({});
        clearValidation();
    }, []);
    const input = (label, prop, type) => (jsx(InputText, { "data-test-id": `reset-password-form-input-${replaceAll(label.toLocaleLowerCase(), " ", "")}`, className: classes.input, label: label, type: type, autoCapitalize: "off", value: formData[prop], error: validationResult[prop] ?? postRequestValidation[prop], onChange: e => setFormData(prev => ({ ...prev, [prop]: e.target.value })), onValidate: () => runValidate(prop) }));
    return (jsxs(Box, { className: cx(classes.loginForm, className), ...props, children: [jsx(Typography, { variant: "h2", className: classes.title, children: title }), !requestSent && jsxs(Fragment, { children: [input("New Password", "password", "password"), input("Confirm Password", "passwordConfirmation", "password"), invalidCode && jsx(Alert, { severity: "error", children: "Invalid code provided" }), jsx(Box, { className: classes.buttonContainer, children: jsx(Button, { "data-test-id": "reset-password-form-confirm-new-password-btn", disabled: hasErrors, onClick: () => handleSubmit(), children: "Confirm New Password" }) })] }), requestSent &&
                jsxs(Typography, { variant: "body1", children: ["Your password has been reset. Please ", jsx("span", { "data-test-id": "reset-password-form-login-btn", className: classes.link, onClick: () => onLoginLinkClick(), children: "log in" }), "."] })] }));
};

export { ResetPasswordForm };
